'use client'

import React from 'react'
import { Button } from '@/shared/components/ui/button'
import { Printer, ArrowLeft } from 'lucide-react'

interface JobBoardPrintViewProps {
  weekStart: string
  weekEnd: string
  regionName?: string
  totalVolunteersOnSite: number
  totalVolunteersNeeded: number
  weekDates: Array<{ date: string; dayName: string; dayNumber: string }>
  workOrders: any[]
  onBack: () => void
}

export function JobBoardPrintView({
  weekStart,
  weekEnd,
  regionName,
  totalVolunteersOnSite,
  totalVolunteersNeeded,
  weekDates,
  workOrders,
  onBack,
}: JobBoardPrintViewProps) {
  const handlePrint = () => {
    window.print()
  }

  // Format date range
  const startFmt = new Date(weekStart + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  const endFmt = new Date(weekEnd + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 bg-white text-black min-h-screen">
      {/* Screen-only Controls */}
      <div className="flex items-center justify-between print:hidden border-b pb-4">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="w-4 h-4" />
          Back to Interactive Job Board
        </Button>
        <Button size="sm" onClick={handlePrint} className="gap-1.5 bg-primary text-white font-bold">
          <Printer className="w-4 h-4" />
          Print Job Board
        </Button>
      </div>

      {/* Print Header */}
      <div className="border-b-2 border-black pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">ADVENTURES RELIEF — WEEKLY JOB BOARD</h1>
            <p className="text-sm font-medium text-gray-700">
              Week of {startFmt} – {endFmt} {regionName ? `• ${regionName}` : ''}
            </p>
          </div>
          <div className="text-right text-xs">
            <div>Volunteers on Site: <strong>{totalVolunteersOnSite}</strong></div>
            <div>Total Headcount Needed: <strong>{totalVolunteersNeeded}</strong></div>
          </div>
        </div>
      </div>

      {/* Grid Table */}
      <table className="w-full border-collapse border-2 border-black text-xs">
        <thead>
          <tr className="bg-gray-100 border-b-2 border-black">
            <th className="border border-black p-2 text-left w-48">Team Leader / Crew</th>
            {weekDates.map((d) => (
              <th key={d.date} className="border border-black p-2 text-center">
                <div className="uppercase font-bold">{d.dayName}</div>
                <div className="text-gray-600">{d.dayNumber}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workOrders.map((wo) => {
            const homeownerName =
              wo.home?.homeowner?.user?.fullname || wo.home?.homeowner?.name || 'Home'
            const leaderName = wo.teamLead?.fullname || 'Unassigned Leader'
            const workScopeName = wo.workScope?.workScopeDisplay || wo.workOrderType || 'Work Order'

            const activeDates = Array.isArray(wo.workOrderHours)
              ? wo.workOrderHours.map((h: any) => h.date)
              : wo.dateDelegated
                ? [wo.dateDelegated]
                : []

            return (
              <tr key={wo.id} className="border-b border-black">
                <td className="border border-black p-2 font-medium bg-gray-50 align-top">
                  <div className="font-bold text-sm">{leaderName}</div>
                  <div className="text-gray-600">Need: {wo.volunteersNeeded || 0} vols</div>
                  <div className="text-[10px] text-gray-500 mt-1 truncate">{wo.teamName}</div>
                </td>

                {weekDates.map((d) => {
                  const isActive = activeDates.includes(d.date)
                  if (!isActive) {
                    return <td key={d.date} className="border border-black p-1 bg-gray-50/50"></td>
                  }

                  let assignedNames: string[] = []
                  if (Array.isArray(wo.workOrderHours)) {
                    const entry = wo.workOrderHours.find((h: any) => h.date === d.date)
                    if (entry && Array.isArray(entry.volunteerIds) && Array.isArray(wo.volunteers)) {
                      assignedNames = wo.volunteers
                        .filter((v: any) => entry.volunteerIds.includes(v.id))
                        .map((v: any) => v.fullname || v.name)
                    }
                  }

                  return (
                    <td key={d.date} className="border border-black p-2 align-top bg-white">
                      <div className="font-bold text-gray-900">{homeownerName}</div>
                      <div className="text-[11px] font-semibold text-gray-700">{workScopeName}</div>
                      {wo.home?.address && (
                        <div className="text-[10px] text-gray-600 mt-0.5">{wo.home.address}</div>
                      )}
                      {assignedNames.length > 0 && (
                        <div className="mt-1.5 pt-1 border-t border-gray-200 text-[10px]">
                          <strong>Staffed ({assignedNames.length}):</strong>
                          <div className="text-gray-700">{assignedNames.join(', ')}</div>
                        </div>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
